/*
  POPPO cart drawer: slide-out cart with upsells.
  Markup: snippets/poppo-cart-drawer.liquid. Styles: assets/poppo-cart-drawer.css.

  - Intercepts every plain /cart/add form with ONE document-level submit listener in the bubble phase, so it runs
    after a form's own submit handler (the product page rewrites its items[n] inputs there).
  - Forms that Horizon already handles with AJAX (product-form-component, on:submit) are left alone; the drawer
    opens from the standard shopify:cart:lines-update event they dispatch instead.
  - The header cart icon and links to /cart open this drawer instead of Horizon's.
  - Everything shown comes from /cart.js and /products/<handle>.js. No timers that pretend the cart expires.
*/
(function () {
  'use strict';

  /* ---------------- Business constants ---------------- */
  var FREE_SHIPPING_THRESHOLD = 0; // in cents. 0 means every order ships free.
  var UPSELL_HANDLES = ['korean-scalp-conditioner', 'korean-scalp-revival-oil', 'scalp-revival-brush', 'bamboo-draining-dock'];
  var UPSELL_MAX = 2;
  var MYSTERY_GIFT_PROPERTY = 'Free mystery gift';
  var MYSTERY_GIFT_VALUE = 'Included — 1 per order';

  var root = document.getElementById('PoppoCartDrawer');
  if (!root || root.getAttribute('data-pcd-ready')) return;
  if (!window.fetch || !window.Promise || !window.FormData) return; // leave the native flow untouched
  root.setAttribute('data-pcd-ready', '1');

  /* ---------------- Elements ---------------- */
  function q(sel) {
    return root.querySelector(sel);
  }
  var panel = q('.pcd__panel');
  var closeBtn = q('.pcd__close');
  var elBody = q('[data-pcd-body]');
  var elError = q('[data-pcd-error]');
  var elErrorText = q('[data-pcd-error-text]');
  var elLoading = q('[data-pcd-loading]');
  var elShip = q('[data-pcd-ship]');
  var elItems = q('[data-pcd-items]');
  var elUps = q('[data-pcd-upsells]');
  var elUpsList = q('[data-pcd-upsell-list]');
  var elEmpty = q('[data-pcd-empty]');
  var elFoot = q('[data-pcd-foot]');
  var codeForm = q('[data-pcd-code]');
  var codeInput = q('#PoppoCartDrawerCode');
  var codeApply = q('[data-pcd-apply]');
  var elCodeMsg = q('[data-pcd-code-msg]');
  var elCodes = q('[data-pcd-codes]');
  var elCheckout = q('[data-pcd-checkout]');
  var elTotal = q('[data-pcd-total]');
  var elLive = q('[data-pcd-live]');

  /* ---------------- Routes ---------------- */
  function trimSlash(s) {
    s = String(s || '');
    while (s.length > 1 && s.charAt(s.length - 1) === '/') s = s.slice(0, -1);
    return s;
  }
  var ds = root.dataset;
  var ROOT = trimSlash(ds.rootUrl || '/');
  var ROUTES = {
    root: ROOT === '/' ? '' : ROOT,
    cart: trimSlash(ds.cartUrl || '/cart'),
    add: trimSlash(ds.cartAddUrl || '/cart/add'),
    change: trimSlash(ds.cartChangeUrl || '/cart/change'),
    update: trimSlash(ds.cartUpdateUrl || '/cart/update'),
    checkout: '/checkout'
  };
  var LOCALE = ds.locale || document.documentElement.lang || 'en-US';
  var GIFT_VARIANT_ID = String(ds.giftVariantId || '');
  var GIFT_HANDLE = String(ds.giftHandle || '');
  var GIFT_BUNDLE_VARIANT_IDS = String(ds.giftBundleVariantIds || '')
    .split(',')
    .map(function (id) {
      return id.trim();
    })
    .filter(Boolean);
  var MYSTERY_VARIANT_IDS = String(ds.mysteryVariantIds || '')
    .split(',')
    .map(function (id) {
      return id.trim();
    })
    .filter(Boolean);

  /* ---------------- State ---------------- */
  var state = {
    cart: null,
    open: false,
    busy: false,
    adding: false,
    lastFocus: null,
    pendingCode: '',
    closeTimer: 0
  };
  var productCache = {}; // handle -> Promise<product|null>
  var productData = {}; // handle -> product|null (resolved)

  /* ---------------- Helpers ---------------- */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  var moneyFmt = null;
  function money(cents) {
    var cur = (state.cart && state.cart.currency) || ds.currency || 'USD';
    if (!moneyFmt || moneyFmt.cur !== cur) {
      try {
        moneyFmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: cur });
      } catch (e) {
        moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: cur });
      }
      moneyFmt.cur = cur;
    }
    return moneyFmt.format((Number(cents) || 0) / 100);
  }

  function sized(url, width) {
    if (!url) return '';
    var u = String(url);
    if (u.indexOf('//') === 0) u = 'https:' + u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function request(url, opts) {
    var o = opts || {};
    o.credentials = 'same-origin';
    o.headers = o.headers || { Accept: 'application/json' };
    return fetch(url, o).then(function (res) {
      return res
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          if (!res.ok) {
            var msg = data && (data.description || data.message);
            if (msg && typeof msg !== 'string') msg = null;
            var err = new Error(msg || 'Something went wrong. Please try again.');
            err.status = res.status;
            throw err;
          }
          return data;
        });
    });
  }

  function postJSON(url, body) {
    return request(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
  }

  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = request(ROUTES.root + '/products/' + encodeURIComponent(handle) + '.js')
        .catch(function () {
          return null;
        })
        .then(function (p) {
          productData[handle] = p;
          return p;
        });
    }
    return productCache[handle];
  }

  // Loads the product JSON needed for compare-at prices and upsells, so render() can stay synchronous.
  function hydrate(cart) {
    var handles = UPSELL_HANDLES.slice();
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });
    return Promise.all(handles.map(getProduct)).then(function () {
      return cart;
    });
  }

  function setCart(cart) {
    state.cart = cart;
    render();
    updateBubble(cart.item_count);
    return cart;
  }

  function refresh() {
    return request(ROUTES.cart + '.js').then(hydrate).then(setCart);
  }

  function announce(msg) {
    elLive.textContent = '';
    setTimeout(function () {
      elLive.textContent = msg;
    }, 60);
  }

  function showError(msg) {
    elErrorText.textContent = msg || 'Something went wrong. Please try again.';
    elError.hidden = false;
    elBody.scrollTop = 0;
  }
  function hideError() {
    elError.hidden = true;
    elErrorText.textContent = '';
  }

  function setLoading(on) {
    root.classList.toggle('is-loading', !!on);
    panel.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* ---------------- Icons ---------------- */
  var ICON_TAG =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>';
  var ICON_TRASH =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/></svg>';
  var ICON_MINUS =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 12h14"/></svg>';
  var ICON_PLUS =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>';
  var ICON_X =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var ICON_TRUCK =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="display:inline-block;vertical-align:-4px;margin-right:6px"><path d="M1 7h12v9H1zM13 10h4l3 3v3h-7z"/><circle cx="5.5" cy="18" r="2"/><circle cx="16.5" cy="18" r="2"/></svg>';
  var PLACEHOLDER =
    '<span class="pcd__ph" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M12 21c-4.4 0-7-3-7-7 0-5 7-11 7-11s7 6 7 11c0 4-2.6 7-7 7z"/><path d="M12 21V11"/></svg></span>';

  /* ---------------- Rendering ---------------- */
  function variantOf(item) {
    var p = productData[item.handle];
    if (!p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].id === item.variant_id) return p.variants[i];
    return null;
  }

  // Line-level discounts that actually reduce this line (a zero allocation only marks the qualifying item).
  function discountTitles(item) {
    var out = [];
    (item.line_level_discount_allocations || []).forEach(function (a) {
      var t = a && a.discount_application && a.discount_application.title;
      if (t && Number(a.amount) > 0 && out.indexOf(t) === -1) out.push(t);
    });
    return out;
  }

  function itemHTML(item) {
    var key = item.key;
    var title = item.product_title || item.title;
    var v = variantOf(item);
    var finalPrice = Number(item.final_line_price) || 0;
    var compare = Math.max(Number(item.original_line_price) || 0, v && v.compare_at_price ? v.compare_at_price * item.quantity : 0);
    var isFree = finalPrice === 0;
    var imgUrl = (item.featured_image && item.featured_image.url) || item.image;
    var meta = [];
    if (!item.product_has_only_default_variant && item.variant_title) meta.push(esc(item.variant_title));
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) meta.push(esc(item.selling_plan_allocation.selling_plan.name));
    var props = item.properties || {};
    Object.keys(props).forEach(function (k) {
      var val = props[k];
      if (!k || k.charAt(0) === '_' || val == null || val === '') return;
      meta.push(esc(k) + ': ' + esc(val));
    });
    var chips = discountTitles(item)
      .map(function (t) {
        return '<li class="pcd__chip">' + ICON_TAG + '<span>' + esc(t) + '</span></li>';
      })
      .join('');
    var url = item.url || '#';
    var minus = item.quantity - 1;

    return (
      '<li class="pcd__item" data-key="' + esc(key) + '">' +
        '<a class="pcd__thumb" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' +
          (imgUrl ? '<img src="' + esc(sized(imgUrl, 160)) + '" alt="" width="72" height="72" loading="lazy">' : PLACEHOLDER) +
        '</a>' +
        '<div class="pcd__info">' +
          '<div class="pcd__row">' +
            '<div>' +
              '<p class="pcd__title"><a href="' + esc(url) + '">' + esc(title) + '</a></p>' +
              (meta.length ? '<p class="pcd__meta">' + meta.join(' &middot; ') + '</p>' : '') +
            '</div>' +
            '<button type="button" class="pcd__rm" data-pcd-act="qty" data-key="' + esc(key) + '" data-qty="0" data-pcd-focus="rm:' + esc(key) + '" aria-label="Remove ' + esc(title) + '">' + ICON_TRASH + '</button>' +
          '</div>' +
          '<div class="pcd__row pcd__row--bottom">' +
            '<div class="pcd__qty" role="group" aria-label="Quantity for ' + esc(title) + '">' +
              '<button type="button" data-pcd-act="qty" data-key="' + esc(key) + '" data-qty="' + minus + '" data-pcd-focus="dec:' + esc(key) + '" aria-label="' + (minus === 0 ? 'Remove ' + esc(title) : 'Decrease quantity') + '">' + ICON_MINUS + '</button>' +
              '<span class="pcd__qty-n">' + item.quantity + '</span>' +
              '<button type="button" data-pcd-act="qty" data-key="' + esc(key) + '" data-qty="' + (item.quantity + 1) + '" data-pcd-focus="inc:' + esc(key) + '" aria-label="Increase quantity">' + ICON_PLUS + '</button>' +
            '</div>' +
            '<div class="pcd__price">' +
              (compare > finalPrice ? '<s class="pcd__was"><span class="pcd__sr">Regular price </span>' + money(compare) + '</s>' : '') +
              '<span class="pcd__now' + (isFree ? ' is-free' : '') + '">' + (compare > finalPrice ? '<span class="pcd__sr">Your price </span>' : '') + (isFree ? 'FREE' : money(finalPrice)) + '</span>' +
            '</div>' +
          '</div>' +
          (chips ? '<ul class="pcd__chips" aria-label="Discounts">' + chips + '</ul>' : '') +
        '</div>' +
      '</li>'
    );
  }

  function renderShip(cart) {
    var t = FREE_SHIPPING_THRESHOLD;
    var sub = Number(cart.total_price) || 0;
    var unlocked = t <= 0 || sub >= t;
    var pct = unlocked ? 100 : Math.max(0, Math.min(100, Math.round((sub / t) * 100)));
    var text = unlocked
      ? 'Congrats! You&rsquo;ve unlocked <strong>FREE SHIPPING!</strong>'
      : 'You&rsquo;re <strong>' + money(t - sub) + '</strong> away from <strong>FREE SHIPPING</strong>';
    elShip.innerHTML =
      '<p class="pcd__ship-text">' + ICON_TRUCK + text + '</p>' +
      '<div class="pcd__bar" aria-hidden="true"><span style="width:' + pct + '%"></span></div>';
  }

  function upsellCandidates(cart) {
    var inCart = {};
    (cart.items || []).forEach(function (i) {
      inCart[i.product_id] = true;
    });
    var list = [];
    UPSELL_HANDLES.forEach(function (h) {
      if (list.length >= UPSELL_MAX) return;
      var p = productData[h];
      if (!p || !p.available || inCart[p.id]) return;
      var v = null;
      (p.variants || []).some(function (x) {
        if (x.available) v = x;
        return !!v;
      });
      if (v) list.push({ handle: h, p: p, v: v });
    });
    return list;
  }

  function renderUpsells(cart) {
    var list = cart.item_count > 0 ? upsellCandidates(cart) : [];
    if (!list.length) {
      elUps.hidden = true;
      elUpsList.innerHTML = '';
      return;
    }
    elUpsList.classList.toggle('is-single', list.length === 1);
    elUpsList.innerHTML = list
      .map(function (u) {
        var p = u.p;
        var v = u.v;
        var img = p.featured_image || (p.images && p.images[0]);
        var url = ROUTES.root + '/products/' + encodeURIComponent(u.handle);
        return (
          '<li class="pcd__up">' +
            '<a class="pcd__up-media" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' +
              (img ? '<img src="' + esc(sized(img, 360)) + '" alt="" width="180" height="180" loading="lazy">' : PLACEHOLDER) +
            '</a>' +
            '<p class="pcd__up-title"><a href="' + esc(url) + '">' + esc(p.title) + '</a></p>' +
            '<p class="pcd__up-price"><span>' + money(v.price) + '</span>' +
              (v.compare_at_price > v.price ? '<s><span class="pcd__sr">Regular price </span>' + money(v.compare_at_price) + '</s>' : '') +
            '</p>' +
            '<button type="button" class="pcd__btn" data-pcd-act="up" data-variant="' + esc(v.id) + '" data-pcd-focus="up:' + esc(u.handle) + '" aria-label="Add ' + esc(p.title) + ' to cart">Add</button>' +
          '</li>'
        );
      })
      .join('');
    elUps.hidden = false;
  }

  function appliedCodes(cart) {
    return (cart.discount_codes || [])
      .filter(function (d) {
        return d && d.applicable;
      })
      .map(function (d) {
        return d.code;
      });
  }

  function codeAmount(cart, code) {
    var total = 0;
    var lc = String(code).toLowerCase();
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a.type === 'discount_code' && String(a.title).toLowerCase() === lc) total += Number(a.total_allocated_amount) || 0;
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (al) {
        var a = al.discount_application || {};
        if (a.type === 'discount_code' && String(a.title).toLowerCase() === lc) total += Number(al.amount) || 0;
      });
    });
    return total;
  }

  function renderCodes(cart) {
    var html = '';
    appliedCodes(cart).forEach(function (code) {
      var amt = codeAmount(cart, code);
      html +=
        '<li class="pcd__chip">' + ICON_TAG + '<span>' + esc(code) + (amt > 0 ? ' (&minus;' + money(amt) + ')' : '') + '</span>' +
        '<button type="button" class="pcd__code-x" data-pcd-act="code-rm" data-code="' + esc(code) + '" data-pcd-focus="code-rm:' + esc(code) + '" aria-label="Remove discount ' + esc(code) + '">' + ICON_X + '</button></li>';
    });
    // Automatic order-level discounts, shown for transparency (they cannot be removed).
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a.type === 'discount_code' || !a.title) return;
      html +=
        '<li class="pcd__chip is-auto">' + ICON_TAG + '<span>' + esc(a.title) + (a.total_allocated_amount > 0 ? ' (&minus;' + money(a.total_allocated_amount) + ')' : '') + '</span></li>';
    });
    if (state.pendingCode) {
      html += '<li class="pcd__chip is-auto">' + ICON_TAG + '<span>' + esc(state.pendingCode) + ' (applied at checkout)</span></li>';
    }
    elCodes.innerHTML = html;
    elCodes.hidden = !html;
  }

  function renderTotal(cart) {
    elTotal.textContent = money(cart.total_price);
    elCheckout.setAttribute('href', ROUTES.checkout + (state.pendingCode ? '?discount=' + encodeURIComponent(state.pendingCode) : ''));
  }

  function focusToken() {
    var a = document.activeElement;
    if (!a || !root.contains(a)) return null;
    return a.getAttribute('data-pcd-focus') || (a === panel ? '' : null);
  }
  function restoreFocus(token) {
    if (token == null || !state.open) return;
    if (token) {
      var els = root.querySelectorAll('[data-pcd-focus]');
      for (var i = 0; i < els.length; i++) {
        if (els[i].getAttribute('data-pcd-focus') === token) {
          els[i].focus({ preventScroll: true });
          return;
        }
      }
      // The control is gone. A removed discount returns focus to the code field; anything else (line removed,
      // upsell added) goes to the panel, never to a neighbouring control a repeated key press could trigger.
      if (token.indexOf('code-rm:') === 0 && !elFoot.hidden) {
        codeInput.focus({ preventScroll: true });
        return;
      }
    }
    if (!root.contains(document.activeElement) || document.activeElement === document.body) panel.focus({ preventScroll: true });
  }

  function render() {
    var cart = state.cart;
    elLoading.hidden = !!cart;
    if (!cart) return;
    var token = focusToken();
    var empty = !cart.item_count;
    root.classList.toggle('is-empty', empty);
    elEmpty.hidden = !empty;
    elFoot.hidden = empty;
    elShip.hidden = empty;
    elItems.hidden = empty;
    if (empty) {
      elItems.innerHTML = '';
      state.pendingCode = '';
    } else {
      renderShip(cart);
      elItems.innerHTML = cart.items.map(itemHTML).join('');
      renderCodes(cart);
      renderTotal(cart);
    }
    renderUpsells(cart);
    restoreFocus(token);
  }

  /* ---------------- Header cart count (Horizon cart-icon) ---------------- */
  function updateBubble(count) {
    count = Number(count) || 0;
    var icons = document.querySelectorAll('cart-icon');
    for (var i = 0; i < icons.length; i++) {
      var icon = icons[i];
      if (typeof icon.renderCartBubble === 'function') {
        try {
          if (String(icon.currentCartCount) !== String(count)) icon.renderCartBubble(count, true);
          continue;
        } catch (e) {
          /* fall through to the manual update */
        }
      }
      var bubble = icon.querySelector('.cart-bubble');
      var n = icon.querySelector('.cart-bubble__text-count');
      if (n) {
        n.textContent = count > 0 && count < 100 ? String(count) : '';
        n.classList.toggle('hidden', count === 0);
      }
      if (bubble) bubble.classList.toggle('visually-hidden', count === 0);
      icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    }
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (e) {
      /* storage may be unavailable */
    }
  }

  /* ---------------- Open / close, focus trap, scroll lock ---------------- */
  var TRIGGER_SELECTOR = '[aria-controls="cart-drawer"], [aria-controls="PoppoCartDrawer"], [data-pcd-open]';

  function setExpanded(v) {
    var t = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < t.length; i++) if (!root.contains(t[i])) t[i].setAttribute('aria-expanded', v ? 'true' : 'false');
  }

  function lockScroll() {
    var sw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('pcd-lock');
    if (sw > 0) document.body.style.paddingRight = sw + 'px';
  }
  function unlockScroll() {
    document.documentElement.classList.remove('pcd-lock');
    document.body.style.paddingRight = '';
  }

  function focusables() {
    var list = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.closest('[hidden]') || el.closest('[aria-hidden="true"]')) continue;
      if (!el.getClientRects().length) continue;
      out.push(el);
    }
    return out;
  }

  function onKeydown(e) {
    if (!state.open) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    var first = f[0];
    var last = f[f.length - 1];
    var active = document.activeElement;
    if (!panel.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onFocusIn(e) {
    if (state.open && !root.contains(e.target)) {
      var f = focusables();
      (f[0] || panel).focus({ preventScroll: true });
    }
  }

  // skipFetch: the caller refreshes the cart itself (e.g. right after an add), so do not load it twice.
  function open(opener, skipFetch) {
    if (!state.open) {
      clearTimeout(state.closeTimer);
      state.open = true;
      state.lastFocus = opener && opener.focus ? opener : document.activeElement;
      root.hidden = false;
      lockScroll();
      void root.offsetWidth; // commit the start position so the slide-in animates
      root.classList.add('is-open');
      setExpanded(true);
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('focusin', onFocusIn);
      closeBtn.focus({ preventScroll: true });
    }
    render();
    if (!state.cart && !skipFetch) {
      setLoading(true);
      refresh()
        .catch(function (err) {
          showError(err.message);
        })
        .then(function () {
          setLoading(false);
        });
    }
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    root.classList.remove('is-open');
    setExpanded(false);
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('focusin', onFocusIn);
    unlockScroll();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    state.closeTimer = setTimeout(
      function () {
        if (!state.open) root.hidden = true;
      },
      reduce ? 0 : 360
    );
    var back = state.lastFocus;
    if (!back || !document.contains(back) || back === document.body) back = document.querySelector(TRIGGER_SELECTOR);
    if (back && back.focus) back.focus({ preventScroll: true });
    state.lastFocus = null;
  }

  /* ---------------- Cart actions ---------------- */
  function withBusy(lineKey, fn) {
    if (state.busy) return Promise.resolve();
    state.busy = true;
    setLoading(true);
    hideError();
    var li = null;
    if (lineKey) {
      var lis = elItems.querySelectorAll('.pcd__item');
      for (var i = 0; i < lis.length; i++) if (lis[i].getAttribute('data-key') === lineKey) li = lis[i];
      if (li) li.classList.add('is-busy');
    }
    return Promise.resolve()
      .then(fn)
      .catch(function (err) {
        showError(err && err.message);
        return refresh().catch(function () {});
      })
      .then(function () {
        state.busy = false;
        setLoading(false);
        if (li) li.classList.remove('is-busy');
      });
  }

  function changeLine(key, qty) {
    return withBusy(key, function () {
      return postJSON(ROUTES.change + '.js', { id: key, quantity: qty })
        .then(hydrate)
        .then(setCart)
        .then(function () {
          announce(qty === 0 ? 'Item removed from cart' : 'Quantity updated to ' + qty);
        });
    });
  }

  function addVariant(variantId, btn) {
    return withBusy(null, function () {
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = 'Adding...';
      }
      return postJSON(ROUTES.add + '.js', { items: [{ id: Number(variantId), quantity: 1 }] })
        .then(refresh)
        .then(function () {
          announce('Added to cart');
        })
        .catch(function (err) {
          if (btn && document.contains(btn)) {
            btn.removeAttribute('aria-disabled');
            btn.textContent = 'Add';
          }
          throw err;
        });
    });
  }

  function codeMessage(msg, isError) {
    elCodeMsg.textContent = msg || '';
    elCodeMsg.classList.toggle('is-error', !!isError);
    elCodeMsg.hidden = !msg;
  }

  function updateDiscounts(codes) {
    return postJSON(ROUTES.update + '.js', { discount: codes.join(',') });
  }

  function applyCode(raw) {
    var code = String(raw || '').trim();
    if (!code || !state.cart) {
      codeMessage('Enter a discount code.', true);
      return Promise.resolve();
    }
    var existing = appliedCodes(state.cart);
    var lc = code.toLowerCase();
    if (existing.some(function (c) { return c.toLowerCase() === lc; })) {
      codeMessage('Code ' + code + ' is already applied.', false);
      return Promise.resolve();
    }
    codeApply.disabled = true;
    codeMessage('', false);
    return withBusy(null, function () {
      return updateDiscounts(existing.concat(code))
        .then(function (cart) {
          var hit = null;
          if (cart && Array.isArray(cart.discount_codes)) {
            cart.discount_codes.forEach(function (d) {
              if (d && String(d.code).toLowerCase() === lc) hit = d;
            });
          }
          if (!hit) {
            // The endpoint ignored the code: hand it to checkout instead.
            var e = new Error('unsupported');
            e.fallback = true;
            throw e;
          }
          if (hit.applicable === false) {
            codeMessage('Code ' + code + ' is not valid for the items in your cart.', true);
            // Drop the invalid code again so the cart only keeps working codes.
            return updateDiscounts(existing).then(refresh);
          }
          codeInput.value = '';
          state.pendingCode = '';
          codeMessage('Code ' + hit.code + ' applied.', false);
          return refresh();
        })
        .catch(function (err) {
          if (err && (err.fallback || !err.status)) {
            state.pendingCode = code;
            codeInput.value = '';
            codeMessage('Code ' + code + ' will be applied at checkout.', false);
            render();
            return;
          }
          codeMessage((err && err.message) || 'That code could not be applied.', true);
        });
    }).then(function () {
      codeApply.disabled = false;
    });
  }

  function removeCode(code) {
    if (!state.cart) return Promise.resolve();
    codeMessage('', false);
    var rest = appliedCodes(state.cart).filter(function (c) {
      return c !== code;
    });
    return withBusy(null, function () {
      return updateDiscounts(rest)
        .then(refresh)
        .then(function () {
          announce('Discount ' + code + ' removed');
        });
    });
  }

  /* ---------------- Add-to-cart interception ---------------- */
  function isCartAddForm(form) {
    var action = form.getAttribute('action');
    if (!action) return false;
    var path;
    try {
      var u = new URL(action, location.href);
      if (u.origin !== location.origin) return false;
      path = trimSlash(u.pathname);
    } catch (e) {
      return false;
    }
    return path.slice(-9) === '/cart/add';
  }

  // Horizon product forms (on:submit / product-form-component) already add over AJAX.
  function handledByHorizon(form) {
    for (var el = form; el && el !== document.documentElement; el = el.parentElement) {
      if (el.hasAttribute('on:submit') || el.tagName === 'PRODUCT-FORM-COMPONENT') return true;
    }
    return false;
  }

  function setButtonBusy(btn, on) {
    if (!btn || !btn.classList) return;
    btn.classList.toggle('pcd-adding', on);
    if (on) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('data-pcd-was-disabled', btn.disabled ? '1' : '0');
      btn.disabled = true;
    } else {
      btn.removeAttribute('aria-busy');
      btn.disabled = btn.getAttribute('data-pcd-was-disabled') === '1';
      btn.removeAttribute('data-pcd-was-disabled');
    }
  }

  function formDataEntries(fd) {
    var entries = [];
    fd.forEach(function (value, key) {
      entries.push([key, value]);
    });
    return entries;
  }

  function submittedItemIds(fd) {
    var ids = fd.getAll('id').map(String);
    fd.forEach(function (value, key) {
      if (/^items\[\d+\]\[id\]$/.test(key)) ids.push(String(value));
    });
    return ids;
  }

  function submittedGiftIndexes(fd) {
    var indexes = [];
    fd.forEach(function (value, key) {
      var match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (match && String(value) === GIFT_VARIANT_ID) indexes.push(Number(match[1]));
    });
    return indexes;
  }

  function attachMysteryGiftProperty(fd) {
    var plainBundle = fd.getAll('id').map(String).some(function (id) {
      return MYSTERY_VARIANT_IDS.indexOf(id) > -1;
    });
    if (plainBundle) fd.set('properties[' + MYSTERY_GIFT_PROPERTY + ']', MYSTERY_GIFT_VALUE);

    var bundleIndexes = [];
    fd.forEach(function (value, key) {
      var match = key.match(/^items\[(\d+)\]\[id\]$/);
      if (match && MYSTERY_VARIANT_IDS.indexOf(String(value)) > -1) bundleIndexes.push(Number(match[1]));
    });
    bundleIndexes.forEach(function (index) {
      fd.set('items[' + index + '][properties][' + MYSTERY_GIFT_PROPERTY + ']', MYSTERY_GIFT_VALUE);
    });
    return fd;
  }

  /*
    Rebuild only the item keys while keeping every original value (including File values,
    line-item properties and selling plans). This lets a plain one-item form become one
    atomic bundle + gift request, and closes gaps when a stale automatic gift is removed.
  */
  function rewriteItems(fd, removeIndex, addGift) {
    var entries = formDataEntries(fd);
    var oldIndexes = [];
    entries.forEach(function (entry) {
      var match = entry[0].match(/^items\[(\d+)\](\[.+\])$/);
      if (match && oldIndexes.indexOf(Number(match[1])) === -1) oldIndexes.push(Number(match[1]));
    });
    oldIndexes.sort(function (a, b) {
      return a - b;
    });

    var out = new FormData();
    if (oldIndexes.length) {
      var indexMap = {};
      var nextIndex = 0;
      oldIndexes.forEach(function (oldIndex) {
        if (oldIndex === removeIndex) return;
        indexMap[oldIndex] = nextIndex++;
      });
      entries.forEach(function (entry) {
        var match = entry[0].match(/^items\[(\d+)\](\[.+\])$/);
        if (!match) {
          out.append(entry[0], entry[1]);
          return;
        }
        var oldIndex = Number(match[1]);
        if (oldIndex === removeIndex) return;
        out.append('items[' + indexMap[oldIndex] + ']' + match[2], entry[1]);
      });
      if (addGift) {
        out.append('items[' + nextIndex + '][id]', GIFT_VARIANT_ID);
        out.append('items[' + nextIndex + '][quantity]', '1');
      }
      return out;
    }

    var converted = false;
    entries.forEach(function (entry) {
      var key = entry[0];
      if (addGift && key === 'id' && !converted) {
        out.append('items[0][id]', entry[1]);
        converted = true;
      } else if (addGift && key === 'quantity') {
        out.append('items[0][quantity]', entry[1]);
      } else if (addGift && key === 'selling_plan') {
        out.append('items[0][selling_plan]', entry[1]);
      } else if (addGift && key.indexOf('properties[') === 0) {
        out.append('items[0][properties]' + key.slice(10), entry[1]);
      } else {
        out.append(key, entry[1]);
      }
    });
    if (addGift) {
      out.append('items[1][id]', GIFT_VARIANT_ID);
      out.append('items[1][quantity]', '1');
    }
    return out;
  }

  function giftVerificationError(message) {
    return new Error(message || 'We could not verify the free gift. Nothing was added; please try again.');
  }

  function prepareBundleGift(form, fd) {
    var hasBundle = submittedItemIds(fd).some(function (id) {
      return GIFT_BUNDLE_VARIANT_IDS.indexOf(id) > -1;
    });
    if (!hasBundle) return Promise.resolve(fd);
    if (!GIFT_VARIANT_ID || !GIFT_HANDLE) return Promise.reject(giftVerificationError());

    var cartRequest = request(ROUTES.cart + '.js').catch(function () {
      throw giftVerificationError('We could not check your cart for the free gift. Nothing was added; please try again.');
    });
    var giftRequest = request(ROUTES.root + '/products/' + encodeURIComponent(GIFT_HANDLE) + '.js').catch(function () {
      throw giftVerificationError();
    });

    return Promise.all([cartRequest, giftRequest])
      .then(function (results) {
        var cart = results[0];
        var giftProduct = results[1];
        var giftVariant = giftProduct && giftProduct.variants
          ? giftProduct.variants.find(function (variant) {
              return String(variant.id) === GIFT_VARIANT_ID;
            })
          : null;
        if (!giftVariant || !giftVariant.available) {
          throw giftVerificationError('The free Korean Conditioner is currently unavailable, so this bundle was not added.');
        }

        var giftInCart = (cart.items || []).some(function (item) {
          return Number(item.quantity) >= 1 && (String(item.variant_id) === GIFT_VARIANT_ID || item.handle === GIFT_HANDLE);
        });
        var giftIndexes = submittedGiftIndexes(fd);
        var markedAutomaticGift = String(form.dataset.giftId || '') === GIFT_VARIANT_ID;

        if (giftInCart) {
          return markedAutomaticGift && giftIndexes.length ? rewriteItems(fd, giftIndexes[0], false) : fd;
        }
        if (giftIndexes.length || fd.getAll('id').map(String).indexOf(GIFT_VARIANT_ID) > -1) return fd;
        return rewriteItems(fd, null, true);
      })
      .then(attachMysteryGiftProperty);
  }

  function addFromForm(form, submitter) {
    if (state.adding) return;
    state.adding = true;
    var fd = new FormData(form);
    if (submitter && submitter.name && !fd.has(submitter.name)) fd.append(submitter.name, submitter.value || '');
    setButtonBusy(submitter, true);
    hideError();
    open(submitter || form, true);
    setLoading(true);
    prepareBundleGift(form, fd)
      .then(function (prepared) {
        return request(ROUTES.add + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: prepared
        });
      })
      .then(function () {
        return refresh().then(function () {
          announce('Added to cart');
        });
      })
      .catch(function (err) {
        showError(err && err.message);
        return refresh().catch(function () {});
      })
      .then(function () {
        state.adding = false;
        setLoading(false);
        setButtonBusy(submitter, false);
      });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (e.defaultPrevented || root.contains(form)) return;
    if (form.hasAttribute('data-pcd-skip') || form.target === '_blank') return;
    if (!isCartAddForm(form) || handledByHorizon(form)) return;
    e.preventDefault();
    addFromForm(form, e.submitter || null);
  });

  // Adds made by Horizon components: open this drawer instead of Horizon's.
  document.addEventListener('shopify:cart:lines-update', function (e) {
    var isAdd = e.action === 'add';
    var source = e.target instanceof Element ? e.target : null;
    Promise.resolve(e.promise)
      .then(function (res) {
        if (res && res.detail && res.detail.didError) return;
        if (!isAdd) {
          if (state.cart) return refresh();
          return;
        }
        var modal = source && source.closest('dialog[open]');
        var go = function () {
          open(null, true);
          setLoading(true);
          return refresh()
            .catch(function (err) {
              showError(err.message);
            })
            .then(function () {
              setLoading(false);
            });
        };
        if (modal && !root.contains(modal)) modal.addEventListener('close', go, { once: true });
        else return go();
      })
      .catch(function () {
        /* Horizon reports its own errors inline */
      });
  });

  /* ---------------- Triggers: header cart icon and /cart links ---------------- */
  function isCartLink(a) {
    if (!a.href || a.target === '_blank' || a.hasAttribute('download')) return false;
    try {
      var u = new URL(a.href, location.href);
      return u.origin === location.origin && trimSlash(u.pathname) === ROUTES.cart && !u.search && !u.hash;
    } catch (e) {
      return false;
    }
  }

  // Capture phase on window, so Horizon's delegated on:click handler (which would open its own drawer) never runs.
  window.addEventListener(
    'click',
    function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!(e.target instanceof Element) || root.contains(e.target)) return;
      var t = e.target.closest(TRIGGER_SELECTOR + ', a[href]');
      if (!t) return;
      if (t.tagName === 'A' && !t.matches(TRIGGER_SELECTOR) && !isCartLink(t)) return;
      e.preventDefault();
      e.stopPropagation();
      if (state.open) close();
      else open(t);
    },
    true
  );

  /* ---------------- Drawer UI events ---------------- */
  root.addEventListener('click', function (e) {
    var t = e.target instanceof Element ? e.target.closest('[data-pcd-close], [data-pcd-act]') : null;
    if (!t || !root.contains(t)) return;
    if (t.hasAttribute('data-pcd-close')) {
      e.preventDefault();
      close();
      return;
    }
    var act = t.getAttribute('data-pcd-act');
    if (act === 'qty') changeLine(t.getAttribute('data-key'), Math.max(0, parseInt(t.getAttribute('data-qty'), 10) || 0));
    else if (act === 'up') addVariant(t.getAttribute('data-variant'), t);
    else if (act === 'code-rm') removeCode(t.getAttribute('data-code'));
    else if (act === 'dismiss') {
      hideError();
      panel.focus({ preventScroll: true });
    }
  });

  codeForm.addEventListener('submit', function (e) {
    e.preventDefault();
    applyCode(codeInput.value);
  });

  // Images that fail to load (or products without one) fall back to a neutral placeholder.
  root.addEventListener(
    'error',
    function (e) {
      var img = e.target;
      if (img && img.tagName === 'IMG' && root.contains(img)) {
        var holder = document.createElement('span');
        holder.innerHTML = PLACEHOLDER;
        img.replaceWith(holder.firstChild);
      }
    },
    true
  );

  window.addEventListener('pageshow', function (e) {
    if (e.persisted && state.cart) refresh().catch(function () {});
  });

  /* ---------------- Init ---------------- */
  // Keep Horizon's own drawer from auto-opening on top of this one.
  var horizonDrawer = document.querySelectorAll('cart-drawer-component[auto-open]');
  for (var i = 0; i < horizonDrawer.length; i++) horizonDrawer[i].removeAttribute('auto-open');

  window.PoppoCartDrawer = {
    open: function () {
      open(null);
    },
    close: close,
    refresh: refresh
  };
})();
